import { useRef, useState } from "react";
import axios from "axios";
import { isValidPhoneNumber } from "react-phone-number-input";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import HoneypotField from "@/components/HoneypotField";
import PhoneInputField from "@/components/PhoneInputField";
import TurnstileWidget from "@/components/TurnstileWidget";
import { useDetectedCountry } from "@/hooks/useDetectedCountry";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fieldLabelStyle = { color: "var(--gg-secondary-text)" };

const BRAND_OPTIONS = [
  "Gopi Gadhvi Group",
  "Monarch Estates",
  "Monarch Design Studio",
  "The Art Forest",
  "The Wishing Well",
  "Other",
];

// Pure helper: shapes the form's local state into the API's expected
// payload shape (extracted so handleSubmit stays a thin orchestrator).
const buildAppointmentPayload = (fields) => ({
  full_name: fields.fullName,
  email: fields.email,
  phone: fields.phone,
  phone_country: fields.phoneCountry,
  phone_dial_code: fields.phoneDialCode,
  brand_or_service: fields.brand || null,
  preferred_date: fields.preferredDate || null,
  preferred_time: fields.preferredTime || null,
  message: fields.message || null,
  website: fields.website,
  turnstile_token: fields.turnstileToken,
  form_rendered_at: fields.formRenderedAt,
});

// Extracted network call so handleSubmit only orchestrates state, not
// request wiring -- also makes this call independently testable.
const submitAppointment = (payload) => axios.post(`${API}/appointments`, payload);

// Pure validity helpers, extracted so the component body doesn't carry
// this branching logic inline -- each is independently readable/testable.
const computePhoneValid = (phone) => !!phone && isValidPhoneNumber(phone);
const computeCanSubmit = ({ fullName, email, phoneValid, turnstileToken, submitting }) =>
  fullName.trim().length > 1 && !!email && phoneValid && !!turnstileToken && !submitting;

// Small presentational sub-component for the post-submit state -- keeps
// this branch's JSX out of the main form's render body entirely.
const AppointmentSuccess = ({ message }) => (
  <div className="flex flex-col items-center text-center py-10" data-testid="appointment-success">
    <CheckCircle2 size={32} style={{ color: "var(--gg-logo-gold)" }} className="mb-4" />
    <p className="text-editorial">{message}</p>
  </div>
);

const AppointmentForm = () => {
  const detectedCountry = useDetectedCountry();
  const turnstileRef = useRef(null);
  const [formRenderedAt] = useState(() => Date.now());

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneCountry, setPhoneCountry] = useState(null);
  const [phoneDialCode, setPhoneDialCode] = useState(null);
  const [brand, setBrand] = useState("");
  const [preferredDate, setPreferredDate] = useState("");
  const [preferredTime, setPreferredTime] = useState("");
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handlePhoneChange = ({ value, country, dialCode }) => {
    setPhone(value || "");
    setPhoneCountry(country);
    setPhoneDialCode(dialCode);
  };

  const phoneValid = computePhoneValid(phone);
  const canSubmit = computeCanSubmit({ fullName, email, phoneValid, turnstileToken, submitting });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const payload = buildAppointmentPayload({
        fullName, email, phone, phoneCountry, phoneDialCode, brand,
        preferredDate, preferredTime, message, website, turnstileToken, formRenderedAt,
      });
      const res = await submitAppointment(payload);
      setSubmitted(true);
      setError(res.data.message);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <AppointmentSuccess message={error} />;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="appointment-form">
      <HoneypotField value={website} onChange={setWebsite} />

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>Full Name</label>
          <Input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your full name" data-testid="appointment-fullname-input" className="neu-inset h-11 font-sans text-[16px]" style={{ color: "var(--gg-ivory)" }} />
        </div>
        <div>
          <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>Email Address</label>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="appointment-email-input" className="neu-inset h-11 font-sans text-[16px]" style={{ color: "var(--gg-ivory)" }} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>Phone Number</label>
          <PhoneInputField value={phone} defaultCountry={detectedCountry} onValueChange={handlePhoneChange} testId="appointment-phone-input" />
          {phone && !phoneValid && (
            <p className="text-small mt-2" style={{ color: "#E39B9B" }} data-testid="appointment-phone-error">Please enter a valid phone number.</p>
          )}
        </div>
        <div>
          <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>Brand or Service</label>
          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger data-testid="appointment-brand-select" className="neu-inset h-11 font-sans text-[16px]" style={{ color: "var(--gg-ivory)" }}>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent style={{ background: "var(--gg-midnight-black)", borderColor: "rgba(42,42,42,0.85)", color: "var(--gg-pearl)" }}>
              {BRAND_OPTIONS.map((b) => (
                <SelectItem key={b} value={b} data-testid={`appointment-brand-option-${b.toLowerCase().replace(/\s+/g, "-")}`}>{b}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>Preferred Date <span style={{ color: "var(--gg-muted-text)" }}>(optional)</span></label>
          <Input type="date" value={preferredDate} onChange={(e) => setPreferredDate(e.target.value)} data-testid="appointment-date-input" className="neu-inset h-11 font-sans text-[16px]" style={{ color: "var(--gg-ivory)" }} />
        </div>
        <div>
          <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>Preferred Time <span style={{ color: "var(--gg-muted-text)" }}>(optional)</span></label>
          <Input type="time" value={preferredTime} onChange={(e) => setPreferredTime(e.target.value)} data-testid="appointment-time-input" className="neu-inset h-11 font-sans text-[16px]" style={{ color: "var(--gg-ivory)" }} />
        </div>
      </div>

      <div>
        <label className="text-small font-semibold uppercase tracking-wide block mb-2" style={fieldLabelStyle}>Message <span style={{ color: "var(--gg-muted-text)" }}>(Optional)</span></label>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Anything you'd like us to know ahead of time..." rows={4} data-testid="appointment-message-input" className="neu-inset font-sans text-[16px] resize-none" style={{ color: "var(--gg-ivory)" }} />
      </div>

      <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} onExpire={() => setTurnstileToken("")} testId="appointment-turnstile" />

      {error && !submitted && (
        <p className="text-small" style={{ color: "#E39B9B" }} data-testid="appointment-error">{error}</p>
      )}

      <button type="submit" disabled={!canSubmit} className="btn-solid-pearl w-full uppercase tracking-wide text-[0.95rem]" data-testid="appointment-submit-btn">
        {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
        Request Appointment
      </button>
    </form>
  );
};

export default AppointmentForm;
